import React from 'react';
import {
  Box,
  Flex,
  Text,
  Button,
  Checkbox,
  ModalFooter,
  InputGroup,
  InputLeftElement,
  Input,
  SimpleGrid,
  Stack,
  Radio,
  RadioGroup,
  SkeletonText,
} from '@chakra-ui/react';
import { SearchIcon } from '@chakra-ui/icons';
import GlobalModal from './GlobalModal';
import InterfaceSelect from './select'; // Adjust import if needed
import Default from './fields/InputField';

export default function ClusterModal({
  isOpen,
  onClose,
  title,
  mainInputLabel,
  mainInputValue,
  onMainInputChange,
  searchType,
  onSearchTypeChange,
  serialNumber,
  onSerialNumberChange,
  onSerialNumberSearch,
  clusterOptions,
  selectedCluster,
  onClusterChange,
  stateOptions,
  selectedState,
  onStateChange,
  nocOptions,
  selectedNoc,
  onNocChange,
  plantOptions,
  selectedPlant,
  onPlantChange,
  devicesArray,
  selectedDevices,
  onDeviceToggle,
  onSelectAllDevices,
  loadingDevices,
  onLoadMore,
  onSave,
  saveDisabled,
  inputBg,
  textColor,
  footerError,
  footerErrorText,
  deviceId = { deviceId },
  showMainInput = true,
  preselectedDevices = [],
  disablePreselectedDevices = false,
  showMainInputDisabled = false,
  disableCurrentDevice = true,

  minWidth = '1500px',
  ...modalProps
}) {
  // Calculate if save button should be disabled
  const isSaveDisabled = React.useMemo(() => {
    // If showMainInput is false, always enable the save button
    if (!showMainInput) {
      return false;
    }
    // Otherwise use the passed saveDisabled prop
    return saveDisabled;
  }, [showMainInput, saveDisabled]);

  return (
    <GlobalModal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      minWidth={minWidth}
      footer={
        <ModalFooter flexDirection="column" alignItems="center">
          {footerError && (
            <Text fontSize="sm" color="red.500" mb={2}>
              {footerErrorText}
            </Text>
          )}
          <Box display="flex" w="100%" justifyContent="center">
            <Button
              size="sm"
              colorScheme="orange"
              variant="outline"
              border="2px"
              cursor="pointer"
              mr={3}
              onClick={onLoadMore}
              isLoading={loadingDevices}
              loadingText="Loading..."
              spinnerPlacement="start"
              isDisabled={serialNumber || !onLoadMore}
            >
              Load More
            </Button>
            <Button
              size="sm"
              colorScheme="green"
              variant="outline"
              border="2px"
              isDisabled={isSaveDisabled} // Use our calculated value
              onClick={onSave}
              cursor="pointer"
              mr={3}
            >
              Save
            </Button>
            <Button
              size="sm"
              colorScheme="red"
              variant="outline"
              border="2px"
              cursor="pointer"
              mr={3}
              onClick={onClose}
            >
              Close
            </Button>
          </Box>
        </ModalFooter>
      }
      {...modalProps}
    >
      <Box position="relative">
        {/* Filter By placed in header row */}
        <Flex align="center" gap={5} position="absolute" top="-48px" left="32%">
          <Text fontWeight={600}>Filter By:</Text>
          <RadioGroup value={searchType} onChange={onSearchTypeChange}>
            <Stack direction="row">
              <Radio value="deviceId">Device ID</Radio>
              <Radio value="cluster">Cluster</Radio>
            </Stack>
          </RadioGroup>
        </Flex>
        <Flex
          justifyContent="space-between"
          alignItems="center"
          flexWrap="wrap"
        >
          {/* Main Input + Select All - Conditionally rendered */}
          <Flex alignItems="center" gap={4}>
            {showMainInput && (
              <Default
                maxWidth="300px"
                fontSize="lg"
                border="2px"
                placeholder={mainInputLabel}
                value={mainInputValue}
                onChange={onMainInputChange}
                bg={inputBg}
                color={textColor}
              />
            )}
            <Checkbox
              isChecked={
                devicesArray.length > 0 &&
                devicesArray.filter((d) => !d.disabled).length > 0 &&
                devicesArray
                  .filter((d) => !d.disabled)
                  .every((d) => selectedDevices.includes(d.id))
              }
              isIndeterminate={
                devicesArray.filter((d) => !d.disabled).length > 0 &&
                devicesArray
                  .filter((d) => !d.disabled)
                  .some((d) => selectedDevices.includes(d.id)) &&
                !devicesArray
                  .filter((d) => !d.disabled)
                  .every((d) => selectedDevices.includes(d.id))
              }
              onChange={onSelectAllDevices}
              isDisabled={devicesArray.length === 0}
            >
              Select All
            </Checkbox>
          </Flex>
          {/* Cluster Dropdowns */}
          {searchType === 'cluster' && (
            <Flex gap={2} alignItems="center" mb={2}>
              <Text fontWeight="bold" minW="60px">
                Country:
              </Text>
              <InterfaceSelect
                placeholder="Cluster"
                value={selectedCluster}
                onChange={onClusterChange}
                options={clusterOptions}
                isDisabled={loadingDevices || clusterOptions.length === 0}
                width="180px"
              />
              <Text fontWeight="bold" minW="50px" ml={2}>
                State:
              </Text>
              <InterfaceSelect
                placeholder="State"
                value={selectedState}
                onChange={onStateChange}
                options={stateOptions}
                isDisabled={
                  !selectedCluster ||
                  loadingDevices ||
                  stateOptions.length === 0
                }
                width="180px"
              />
              <Text fontWeight="bold" minW="40px" ml={2}>
                NOC:
              </Text>
              <InterfaceSelect
                placeholder="NOC"
                value={selectedNoc}
                onChange={onNocChange}
                options={nocOptions}
                isDisabled={
                  !selectedState || loadingDevices || nocOptions.length === 0
                }
                width="150px"
              />
              <Text fontWeight="bold" minW="50px" ml={2}>
                Plant:
              </Text>
              <InterfaceSelect
                placeholder="Plant"
                value={selectedPlant}
                onChange={onPlantChange}
                options={plantOptions}
                isDisabled={
                  !selectedNoc || loadingDevices || plantOptions.length === 0
                }
                width="150px"
              />
            </Flex>
          )}
          {/* Device Search */}
          {searchType === 'deviceId' && (
            <InputGroup width="400px" borderRadius="1rem">
              <InputLeftElement>
                <SearchIcon borderRadius="2rem" color="navy.500" />
              </InputLeftElement>
              <Input
                size="md"
                borderRadius="1rem"
                border="2px lightgray solid"
                type="text"
                value={serialNumber}
                boxShadow="none"
                onChange={onSerialNumberChange}
                onKeyDown={onSerialNumberSearch}
                color={textColor}
                placeholder="Search Device..."
                outline="none"
                _focus={{ boxShadow: 'none' }}
              />
            </InputGroup>
          )}
        </Flex>
        <Box mt="3" p="2">
          {(searchType === 'deviceId' || searchType === 'cluster') &&
            (loadingDevices ? (
              <SimpleGrid columns={4} spacing={4} mt={3}>
                {[...Array(4)].map((_, idx) => (
                  <SkeletonText key={idx} noOfLines={2} spacing="4" />
                ))}
              </SimpleGrid>
            ) : (
              <SimpleGrid columns={4} spacing={4} mt={3}>
                {devicesArray.length === 0 ? (
                  <Text color="gray.500" fontWeight="bold">
                    No devices available
                  </Text>
                ) : (
                  devicesArray.map((d, index) => {
                    const isCurrentDevice =
                      d.id === deviceId || d.name === deviceId;
                    const isPreselected =
                      preselectedDevices.includes(d.id) ||
                      preselectedDevices.includes(d.name);
                    return (
                      <Checkbox
                        isChecked={
                          selectedDevices.includes(d.id) ||
                          selectedDevices.includes(d.name)
                        }
                        isDisabled={
                          (disablePreselectedDevices && isPreselected) ||
                          (disableCurrentDevice && isCurrentDevice)
                        }
                        key={d.id || index}
                        value={d.id}
                        onChange={() => onDeviceToggle(d.id)}
                      >
                        {d.name}
                      </Checkbox>
                    );
                  })
                )}
              </SimpleGrid>
            ))}
        </Box>
      </Box>
    </GlobalModal>
  );
}
